import React, { type ElementType, memo } from 'react';
import { Placement } from 'tippy.js';

import { TooltipType } from '../../types/TooltipType';
import { Popover } from '../Popover/Popover';
import { Text } from '../Text/Text';

type Props = {
  /** The tooltip is triggered by wrapping its children */
  children: React.ReactElement;
  /** If the tooltip can be interacted (i.e. supports pointer events) */
  isInteractive?: boolean;
  /** Renders an inverted (white) tooltip */
  isInverse?: boolean;
  /** Control the visibility explicitly */
  isVisible?: boolean;
  /** Called when the user clicks outside the tooltip. Only relevant when `isVisible` is controlled — pair it with clearing the state that drives `isVisible` so the tooltip can be dismissed. */
  onClickOutside?: (event: MouseEvent) => void;
  /** Tooltip content (prefer simple strings) */
  tooltip?: TooltipType;
  /** Optional styles assigned on the trigger node */
  triggerStyle?: React.CSSProperties;
  /** Optional styles assigned on the popover container node */
  containerStyle?: React.CSSProperties;
  /** Trigger width */
  width?: number | string;
  /** placement of the tooltip */
  placement?: Placement;
  /**
   * Root wrapper around the popover trigger. Default `div` (block). Use `span` inside
   * inline prose so selection and line layout stay continuous.
   */
  wrapperAs?: ElementType;
};

export const Tooltip = memo(
  ({
    children,
    isInteractive = false,
    isInverse,
    isVisible,
    onClickOutside,
    tooltip,
    triggerStyle = {},
    containerStyle = {},
    placement,
    width,
    wrapperAs,
  }: Props) => {
    if (!tooltip) {
      return children;
    }

    const tooltipContent =
      typeof tooltip === 'string' ? (
        <Text color="inherit" variant="tiny" whiteSpace="pre-wrap">
          {tooltip}
        </Text>
      ) : (
        tooltip
      );

    return (
      <Popover
        appendTo={document.body}
        containerStyle={{
          width,
          ...containerStyle,
        }}
        isInteractive={isInteractive}
        isVisible={isVisible}
        onClickOutside={onClickOutside}
        preset={isInverse ? 'tooltip.inverse' : 'tooltip'}
        trigger={children}
        triggerStyle={{
          ...triggerStyle,
          width,
        }}
        placement={placement}
        maxWidth="min(45ch, 90vw)"
        wrapperAs={wrapperAs}
      >
        {tooltipContent}
      </Popover>
    );
  },
);
